import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_ENDPOINTS } from '../constants/api';
import { useAgentContext } from '../contexts/AgentContext';
import { apiService } from '../services/api';
import type { AgentChatMessage } from '../types';
import { genericCopy } from '../utils/userFacingCopy';

function createMessage(role: AgentChatMessage['role'], content: string): AgentChatMessage {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role,
    content,
    createdAt: new Date().toISOString(),
  };
}

export default function AgentChatPanel() {
  const {
    agentEnabled,
    pageContext,
    messages,
    previousResponseId,
    setMessages,
    setPreviousResponseId,
  } = useAgentContext();

  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const suggestedPrompts = useMemo(
    () => (Array.isArray(pageContext.suggestedPrompts) ? pageContext.suggestedPrompts.slice(0, 4) : []),
    [pageContext.suggestedPrompts]
  );

  if (!agentEnabled) {
    return null;
  }

  const handleClose = () => {
    setIsExpanded(false);
  };

  const handleResetConversation = async () => {
    try {
      await apiService.fetchWithAuth(API_ENDPOINTS.AGENT.RESET, {
        method: 'POST',
      });
    } catch {
      // No-op reset endpoint; failing to call should not block local reset.
    }
    setMessages([]);
    setPreviousResponseId(null);
    setError('');
  };

  const sendMessage = async (rawMessage: string) => {
    const message = rawMessage.trim();
    if (!message || sending) return;

    setSending(true);
    setError('');
    const userMessage = createMessage('user', message);
    const assistantMessage = createMessage('assistant', '');
    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await apiService.fetchWithAuth(API_ENDPOINTS.AGENT.CHAT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          message,
          context: {
            pageId: pageContext.pageId,
            pageTitle: pageContext.pageTitle,
            route: pageContext.route,
            viewMode: pageContext.viewMode,
            filters: pageContext.filters,
            visibleSections: pageContext.visibleSections,
          },
          previousResponseId,
        }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        let messageText = genericCopy.assistantSend;
        try {
          const body = await response.json() as { message?: string };
          if (body.message) messageText = body.message;
        } catch {
          // ignore parse error
        }
        throw new Error(messageText);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });

        const events = sseBuffer.split('\n\n');
        sseBuffer = events.pop() ?? '';
        for (const event of events) {
          const lines = event.split('\n').filter((line) => line.startsWith('data: '));
          for (const line of lines) {
            const payload = line.slice(6);
            const parsed = JSON.parse(payload) as { delta?: string; done?: boolean; responseId?: string | null };
            if (parsed.delta) {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantMessage.id ? { ...m, content: `${m.content}${parsed.delta}` } : m))
              );
            }
            if (parsed.done && parsed.responseId) {
              setPreviousResponseId(parsed.responseId);
            }
          }
        }
      }
    } catch (err) {
      const messageText = err instanceof Error && err.message && !err.message.includes('status code')
        ? err.message
        : genericCopy.assistantSend;
      setError(messageText);
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantMessage.id ? { ...m, error: messageText, content: m.content || genericCopy.assistantNoResponse } : m))
      );
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const handleAbort = () => {
    abortRef.current?.abort();
    setSending(false);
  };

  return (
    <>
      {!isExpanded && (
        <IconButton
          onClick={() => setIsExpanded(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            bgcolor: 'primary.main',
            color: 'white',
            width: 64,
            height: 64,
            boxShadow: 3,
            '&:hover': { bgcolor: 'primary.dark', boxShadow: 6 },
          }}
          aria-label="Open Ask Frikkie"
          title="Ask Frikkie"
        >
          <SmartToyIcon />
        </IconButton>
      )}

      {isExpanded && (
        <>
          <Box
            onClick={handleClose}
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
            }}
            aria-hidden="true"
          />
          <Box
            role="dialog"
            aria-modal="true"
            sx={{
              position: 'fixed',
              bottom: { xs: 0, sm: 24 },
              right: { xs: 0, sm: 24 },
              zIndex: 1301,
              bgcolor: 'background.paper',
              borderRadius: { xs: '16px 16px 0 0', sm: 2 },
              boxShadow: 24,
              width: { xs: '100%', sm: 440 },
              maxWidth: { xs: '100%', sm: '90vw' },
              height: { xs: '85vh', sm: '75vh' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src="/aardvark.svg"
                    alt=""
                    sx={{
                      width: 24,
                      height: 24,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  Ask Frikkie
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button size="small" onClick={handleResetConversation} disabled={sending}>
                  New chat
                </Button>
                <IconButton onClick={handleClose} size="small" aria-label="Close Ask Frikkie">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Context: {pageContext.pageTitle ?? pageContext.pageId ?? 'current page'}
              </Typography>
              {suggestedPrompts.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {suggestedPrompts.map((prompt) => (
                    <Chip
                      key={prompt}
                      label={prompt}
                      size="small"
                      onClick={() => void sendMessage(prompt)}
                      disabled={sending}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, sm: 2 }, pb: 1 }} role="log" aria-live="polite">
              {messages.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Ask anything about this page or workflow. Frikkie adapts to your current screen and role.
                </Typography>
              )}
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: message.role === 'user' ? 'primary.light' : 'grey.100',
                    ml: message.role === 'user' ? 4 : 0,
                    mr: message.role === 'assistant' ? 4 : 0,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {message.role === 'user' ? 'You' : 'Assistant'}
                  </Typography>
                  {message.role === 'assistant' ? (
                    <Box sx={{ fontSize: 14 }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content || '...'}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2">{message.content}</Typography>
                  )}
                  {message.role === 'assistant' && message.content && (
                    <IconButton
                      size="small"
                      sx={{ mt: 0.5 }}
                      onClick={async () => navigator.clipboard.writeText(message.content)}
                      aria-label="Copy assistant response"
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>

            {error && (
              <Typography variant="caption" color="error" sx={{ px: 2, pb: 1 }}>
                {error}
              </Typography>
            )}

            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void sendMessage(input);
                    }
                  }}
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Ask about this page..."
                />
                <Button
                  variant="contained"
                  onClick={() => void sendMessage(input)}
                  disabled={sending || !input.trim()}
                >
                  {sending ? <CircularProgress size={18} color="inherit" /> : 'Send'}
                </Button>
              </Box>
              {sending && (
                <Button size="small" sx={{ mt: 1 }} onClick={handleAbort}>
                  Stop
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
